import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { designSystem } from '@yola/ws-ui';
import { i18next, readOnly } from '@yola/ws-sdk';
import bowser from 'yola-bowser';
import segment from 'src/js/modules/analytics/segment';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import user from 'yola-editor/src/js/modules/user';
import BuyDomainContainer from './buy-domain-container';
import UpgradeContainer from './upgrade-container';
import LocalesSelectorContainer from './locales-selector-container';
import BrandContainer from '../../brand/containers/brand-container';
import MainMenuContainer from '../../main-menu/containers/main-menu-container';
import PageButtonContainer from '../../page-manager/containers/page-button-container';
import HelpMenuContainer from '../../help-menu/containers/help-menu-container';
import UndoContainer from '../../undo-redo/containers/undo-container';
import RedoContainer from '../../undo-redo/containers/redo-container';
import SavingContainer from '../../saving/containers/saving-container';
import PublishContainer from '../../publishing/container/publish-container';
import NavigationContainer from '../../navigation/containers/navigation-container';
import WebsiteDesignTrigger from '../components/website-design-trigger';
import OnlineStoreTrigger from '../components/online-store-trigger';
import OrderWebsiteContainer from './order-website-container';
import DeviceModeContainer from './device-mode-container';
import DifmButtonContainer from '../../difm/containers/difm-button-container';
import {
  hasGoldSubscription,
  hasGoldSubscriptionWithoutDifmPromo,
} from '../../difm/verifiers/has-gold-subscription';
import isDifmOrderSubmitted from '../../difm/verifiers/is-difm-order-submitted';
import isOnlineStoreEnabled from '../verifiers/is-online-store-enabled';
import previewMode from '../../preview-mode';
import useMatchBreakpoint from '../../utils/custom-hooks/use-match-breakpoint';
import { saveSiteWithEffect } from '../../saving/helpers/save-site';
import constants from '../constants';
import showReadOnlyModeDialog from '../../read-only-mode/helpers/show-read-only-mode-dialog';

const { EditorHeader } = designSystem;

const {
  trackers: { trackEvent },
  constants: { events, triggerIds },
} = segment;

const NavbarContainer = () => {
  const dispatch = useDispatch();
  const isDesktopBreakpointMatched = useMatchBreakpoint('1200px');
  const isDifmButtonBreakpointMatched = useMatchBreakpoint(constants.SHOW_DIFM_BUTTON_BREAKPOINT);
  const [featureFlags] = useFeatureFlags([
    'difm_latitude',
    'ai_site_wizard',
    'nn_difm_promo',
    'breakpoint_editing',
  ]);
  const {
    difm_latitude: isDifmLatitudeFeatureFlagEnabled,
    nn_difm_promo: isNnDifmPromoFeatureFlagEnabled,
    breakpoint_editing: isBreakpointEditingEnabled,
  } = featureFlags;
  const isWhiteLabel = useSelector(user.selectors.getWhiteLabelStatus);
  const externalUpsellTypes = useSelector(user.selectors.getExternalUpsellTypes);
  const isReadOnlyEnabled = useSelector(readOnly.selectors.getReadOnlyStatus);
  const isDifmAvailable =
    isDifmLatitudeFeatureFlagEnabled &&
    (!isWhiteLabel ||
      externalUpsellTypes.includes(user.constants.externalUpsellTypes.DIFM_SERVICE));
  const isOrderWebsiteShowed = isDesktopBreakpointMatched && isDifmAvailable;

  const isGoldWithoutDifmPromo = hasGoldSubscriptionWithoutDifmPromo();
  const isGold = hasGoldSubscription();
  const isOrderSubmitted = isDifmOrderSubmitted();
  const isDifmButtonDisabled =
    !isNnDifmPromoFeatureFlagEnabled || isGoldWithoutDifmPromo || (!isGold && isOrderSubmitted);

  const isDifmButtonShowed = isDifmButtonBreakpointMatched && !isDifmButtonDisabled;

  const isTouchableDevice =
    bowser.mobile ||
    bowser.tablet ||
    bowser.iPadOS ||
    (bowser.macOS && navigator.maxTouchPoints > 1);
  const isDeviceModeVisible =
    isDesktopBreakpointMatched && isBreakpointEditingEnabled && !isTouchableDevice;

  const onPreviewBtnClick = () => {
    if (isReadOnlyEnabled) {
      showReadOnlyModeDialog();
      return;
    }

    saveSiteWithEffect(async () => {
      dispatch(previewMode.thunks.generateSitePreview());
    });

    trackEvent(events.PREVIEW_TRIGGER_CLICKED, {
      triggerId: triggerIds.EDITOR_HEADER,
    });
  };

  return (
    <EditorHeader>
      <EditorHeader.HeaderLeft>
        <EditorHeader.MenuWrapper>
          <MainMenuContainer />
          <BrandContainer />
        </EditorHeader.MenuWrapper>
        <LocalesSelectorContainer />
        <EditorHeader.Nav>
          <PageButtonContainer />
          <NavigationContainer />
          <WebsiteDesignTrigger />
          {isOnlineStoreEnabled() && <OnlineStoreTrigger />}
          <HelpMenuContainer />
          {isOrderWebsiteShowed && <OrderWebsiteContainer />}
          {isDifmButtonShowed && <DifmButtonContainer />}
        </EditorHeader.Nav>
      </EditorHeader.HeaderLeft>
      <EditorHeader.HeaderRight>
        <EditorHeader.Controls>
          <UndoContainer />
          <RedoContainer />
          <SavingContainer />
        </EditorHeader.Controls>
        {isDeviceModeVisible && (
          <>
            <DeviceModeContainer />
            <EditorHeader.Divider />
          </>
        )}
        <EditorHeader.Actions>
          <EditorHeader.PreviewButton onClick={onPreviewBtnClick} label={i18next.t('Preview')} />
          <BuyDomainContainer />
          <UpgradeContainer />
          <PublishContainer />
        </EditorHeader.Actions>
      </EditorHeader.HeaderRight>
    </EditorHeader>
  );
};

export default NavbarContainer;
